<script lang="tsx">
  import { computed, type PropType } from "vue";

  import { useClientHandle, useQuery } from "@urql/vue";
  import { graphql as gql } from "@/gql";
  import type { FetchSidebarDataQuery, MenuTreeType, Scalars } from "@/gql/graphql";


  interface SidebarTree {
    id: string;
    title?: string;
    url?: string;
    postDate?: string;
    children?: SidebarTree[];
  }

  export default {
    props: {
      entry: {
        type: [Number, String, Boolean] as PropType<Scalars["QueryArgument"]["input"]>,
        required: true,
      },
      site: {
        type: [Number, String] as PropType<Scalars["QueryArgument"]["input"]>,
        required: true,
      },
    },
    async setup(props) {
      const handle = useClientHandle();

      const { data: meta } = await handle.useQuery({
        query: gql(`
          query FetchSidebarSectionMeta($id: QueryArgument, $site: QueryArgument) {
            entry(id: [$id], siteId: [$site]) {
              ... on EntryInterface {
                id,
                structureId,
                level,
                postDate,
                numDescendants: _count(field: "children"),
                sectionId,
                ancestors {
                  id
                },
              }
            }
          }
        `),
        variables: {
          id: props.entry as Scalars["QueryArgument"]["input"],
          site: props.site,
        },
        pause: () => typeof (props.entry) === "object",
      });

      const { data } = await handle.useQuery({
        query: gql(`
          type SidebarNodeType {
            id: ID!,
            title: String!,
            url: String!,
            postDate: DateTime!,
          }

          query FetchSidebarData(
            $site: QueryArgument,
            $firstAncestor: QueryArgument, $isStructure: Boolean!,
            $section: QueryArgument, $postDate: String,
          ) {
            structureTree: entry(id: [$firstAncestor], siteId: [$site]) @include(if: $isStructure) {
              ... on EntryInterface {
                id,
                title,
                url,
                postDate,
                children {
                  id,
                  title,
                  url,
                  postDate
                  children {
                    id,
                    title,
                    url,
                    postDate
                    children {
                      id,
                      title,
                      url,
                      postDate
                      children {
                        id,
                        title,
                        url,
                        postDate
                      }
                    }
                  }
                },
              }
            },
            structure: entry(id: [$firstAncestor], siteId: [$site]) @include(if: $isStructure) {
              ... on EntryInterface {
                ancestors {
                  id
                  title,
                  url,
                  postDate
                },
              }
            }

            channelBefore: entries(sectionId: [$section], siteId: [$site], orderBy: "postDate desc", before: $postDate, limit: 50) @skip(if: $isStructure) {
              ... on EntryInterface {
                id,
                title,
                url,
                postDate,
              }
            }
            channelAfter: entries(sectionId: [$section], siteId: [$site], orderBy: "postDate desc", after: $postDate, limit: 50) @skip(if: $isStructure) {
              ... on EntryInterface {
                id,
                title,
                url,
                postDate,
              }
            }
          }
        `),
        variables: {
          site: props.site,
          firstAncestor: meta.value!.entry!.ancestors[0]?.id ?? meta.value!.entry!.id,
          isStructure: meta.value!.entry!.structureId !== null,
          section: meta.value!.entry!.sectionId,
          postDate: meta.value!.entry!.postDate,
        },
      });

      return {
        data: computed(() => {
          return data.value! as FetchSidebarDataQuery;
        }),
      };
    },
    computed: {
      kind() {
        return this.data!.structure != null ? "structure" : "channel";
      },
      nodes(): SidebarTree[] {
        return this.kind === "structure"
          ? ([this.data.structureTree! ?? {}]) as SidebarTree[]
          // : this.data.channelAfter!.concat(this.data.channelBefore!.slice(1))  as SidebarTree[];
          : this.data.channelAfter!.concat(this.data.channelBefore!)  as SidebarTree[];
      },
    }
  };
</script>

<!-- Making a component for this is stupid, especially when graphql doesn't
support recursion in the first place -->
<template>
  <div class="sidebar">
    <ul class="linktree" v-if="nodes?.length ?? 0 > 0">
      <li v-for="node in nodes">
        <span v-if="node!.url == null">{{ node!.title }}</span>
        <a v-if="node!.url != null" :href="node!.url">{{ node!.title }}</a>

        <ul v-if="node.children?.length ?? 0 > 0">
          <li v-for="node2 in node.children ?? []">
            <span v-if="node2!.url == null">{{ node2!.title }}</span>
            <a v-if="node2!.url != null" :href="node2!.url">{{ node2!.title }}</a>

            <ul v-if="node2.children?.length ?? 0 > 0">
              <li v-for="node3 in node2.children ?? []">
                <span v-if="node3!.url == null">{{ node3!.title }}</span>
                <a v-if="node3!.url != null" :href="node3!.url">{{ node3!.title }}</a>

                <ul v-if="node3.children?.length ?? 0 > 0">
                  <li v-for="node4 in node3.children ?? []">
                    <span v-if="node4!.url == null">{{ node4!.title }}</span>
                    <a v-if="node4!.url != null" :href="node4!.url">{{ node4!.title }}</a>

                    <ul v-if="node4.children?.length ?? 0 > 0">
                      <li v-for="node5 in node4.children ?? []">
                        <span v-if="node5!.url == null">{{ node5!.title }}</span>
                        <a v-if="node5!.url != null" :href="node5!.url">{{ node5!.title }}</a>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style lang="less" scoped>
  @import (reference) "@/style/main.less";

  .sidebar {
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;
    justify-items: stretch;

    background-color: var(--p-surface-100);

    box-shadow:
      calc(var(--spacing-shadow) * -1) 0 var(--spacing-shadow) 0 var(--color-shadow),
      var(--spacing-shadow) 0 var(--spacing-shadow) 0 var(--color-shadow),
      0 var(--spacing-shadow) var(--spacing-shadow) 0 var(--color-shadow),
      0 calc(var(--spacing-shadow) * -1) var(--spacing-shadow) 0 var(--color-shadow);

    @media (width >= @screen-width-medium) {
      border-start-start-radius: var(--spacing-radius);
      border-end-start-radius: var(--spacing-radius);
      margin-inline-start: var(--spacing-shadow);
    }
    @media (width < @screen-width-medium) {
      border-radius: var(--spacing-radius);
    }

    margin-block: var(--spacing-block);
    padding: var(--spacing-block);
  }

  ul {
    list-style-type: square;
  }

  ul ul {
    padding-inline-start: var(--spacing-block);
  }

  .linktree {
    font-size: var(--font-size-small);
  }
</style>
